import { useState, useEffect } from 'react';

import { css } from '@compiled/react';

import AtlasKitProgressBar from '@atlaskit/progress-bar';
import { token } from '@atlaskit/tokens';

import { useCloudStatus } from '../hooks/use-cloud-status';
import { DEFAULT_TIMEOUT } from '../hooks/utils/types';
import { getProgressbarStatus } from '../hooks/utils/use-cloud-status-utils';

const progressStyle = css({
	textAlign: 'center',
	display: 'flex',
	paddingBlock: token('space.0'),
	paddingInline: token('space.0'),
	font: token('font.body'),
	flexDirection: 'column',
	justifyContent: 'center',
	alignSelf: 'center',
	width: '100%',
	maxWidth: '400px',
	marginBlock: token('space.200'),
	marginInline: token('space.200'),
	alignItems: 'center',
});

type Props = {
	isSuccessPreconditionsMet?: boolean;
	/**
	 * When true, the progress bar never renders the red "timeout" state and
	 * continues showing the pending bar even after `createCloudStatus` is
	 * `'timeout'`. Use in flows where a timeout is communicated through
	 * surrounding copy instead of a red bar (e.g. Loom onboarding).
	 */
	suppressTimeoutState?: boolean;
};

export const ProgressBar = ({
	isSuccessPreconditionsMet = true,
	suppressTimeoutState = false,
}: Props) => {
	const { createCloudStatus } = useCloudStatus();
	const [progress, setProgress] = useState(0);

	// components
	const progressTimeout = (
		<AtlasKitProgressBar
			testId="progress-timeout"
			ariaLabel="Account setup progress, taking a little bit longer than expected"
			value={1}
			theme={(getTokens, props) => {
				const tokens = getTokens(props);
				return {
					...tokens,
					bar: {
						...tokens.bar,
						background: token('color.background.danger.bold'),
					},
				};
			}}
		/>
	);
	const progressPending = (
		<AtlasKitProgressBar
			testId="progress-pending"
			ariaLabel="Account setup progress"
			value={progress}
		/>
	);

	// Handles the progress bar update
	useEffect(() => {
		if (createCloudStatus === 'success' && isSuccessPreconditionsMet) {
			setProgress(1);
			return;
		}

		if (createCloudStatus === 'success' && !isSuccessPreconditionsMet) {
			setProgress(0.8);
			return;
		}

		const startTime = Date.now();
		const pivotPercentage = 0.8; // The progress bar will fill up to 80% by the pivot time
		const progressBarPivotPoint = 60 * 1000;
		const slowProgressIncrement =
			(1 - pivotPercentage) / ((DEFAULT_TIMEOUT - progressBarPivotPoint) / 100);
		const slowProgress = 0;

		const interval = setInterval(() => {
			setProgress(
				getProgressbarStatus({
					startTime,
					pivotPercentage,
					progressBarPivotPoint,
					slowProgressIncrement,
					slowProgress,
					interval,
				}),
			);
		}, 100); // Update progress every 0.1 second
		return () => {
			clearInterval(interval);
		};
	}, [createCloudStatus, isSuccessPreconditionsMet]);

	return (
		<div css={progressStyle}>
			{createCloudStatus === 'timeout' && !suppressTimeoutState ? progressTimeout : progressPending}
		</div>
	);
};

export default ProgressBar;
